import { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import BlacksmithWorkshop from '../components/BlacksmithWorkshop';
import { createICSpicyActor } from '../services/icspicy';
import { useAuth } from '../auth/useAuth';

interface LayerFile {
  name: string;
  data: Uint8Array;
}

interface LayerData {
  layer_name: string;
  trait_images: LayerFile[];
}

export default function LayerUploadPage() {
  const { agent, isAuthenticated, loginWithII } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [layers, setLayers] = useState<Map<string, File[]>>(new Map());

  useEffect(() => {
    // no-op: auth is handled globally by AuthProvider
  }, []);

  const handleConnect = async () => {
    try {
      await loginWithII();
    } catch (err: any) {
      setError(err.message || 'Failed to connect');
    }
  };

  const onDrop = useCallback((acceptedFiles: File[], layerName: string) => {
    setLayers(prev => {
      const newLayers = new Map(prev);
      const existing = newLayers.get(layerName) || [];
      newLayers.set(layerName, [...existing, ...acceptedFiles]);
      return newLayers;
    });
  }, []);

  const handleUpload = async () => {
    if (!agent || !isAuthenticated) {
      setError('Please connect wallet first');
      return;
    }

    if (layers.size === 0) {
      setError('Please upload at least one layer');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const actor = await createICSpicyActor(agent);
      
      // Convert layers to ProjectUpload format
      const projectLayers: LayerData[] = [];
      
      for (const [layerName, files] of layers.entries()) {
        const traitImages: LayerFile[] = [];
        
        for (const file of files) {
          const arrayBuffer = await file.arrayBuffer();
          traitImages.push({
            name: file.name,
            data: new Uint8Array(arrayBuffer),
          });
        }
        
        projectLayers.push({
          layer_name: layerName,
          trait_images: traitImages,
        });
      }

      const projectUpload = {
        project_name: "IC Spicy RWA",
        layers: projectLayers,
      };

      const result = await actor.upload_project(projectUpload);
      setSuccess(result);
      setLayers(new Map());
    } catch (err: any) {
      setError(err.message || 'Failed to upload layers');
    } finally {
      setUploading(false);
    }
  };

  const LayerDropzone = ({ layerName }: { layerName: string }) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop: (files) => onDrop(files, layerName),
      accept: {
        'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
      },
    });

    const layerFiles = layers.get(layerName) || [];

    return (
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2 text-gold">{layerName}</h3>
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
            isDragActive
              ? 'border-gold bg-gold/10'
              : 'border-gray-600 hover:border-gray-500'
          }`}
        >
          <input {...getInputProps()} />
          <p className="text-gray-300">
            {isDragActive
              ? 'Drop images here...'
              : `Drag & drop ${layerName} images or click to select`}
          </p>
          {layerFiles.length > 0 && (
            <p className="text-sm text-gray-400 mt-2">
              {layerFiles.length} file(s) selected
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <BlacksmithWorkshop>
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          <div className="glass-dark rounded-2xl p-8 mb-6">
            <h1 className="text-4xl font-bold mb-6 text-gold">Upload Project Layers</h1>
            
            {!isAuthenticated ? (
              <div className="text-center">
                <p className="text-gray-300 mb-4">Please connect your wallet to upload layers</p>
                <button
                  onClick={handleConnect}
                  className="bg-gold hover:bg-yellow-600 text-white font-bold py-3 px-6 rounded-lg transition-all"
                >
                  Connect Wallet
                </button>
              </div>
            ) : (
              <>
                <p className="text-gray-300 mb-6">
                  Upload 10 layer directories. Each layer should contain multiple trait images.
                </p>

                <div className="space-y-4">
                  {[
                    'Background',
                    'Base',
                    'Eyes',
                    'Mouth',
                    'Accessories',
                    'Special',
                    'Layer7',
                    'Layer8',
                    'Layer9',
                    'Layer10',
                  ].map((layerName) => (
                    <LayerDropzone key={layerName} layerName={layerName} />
                  ))}
                </div>

                <button
                  onClick={handleUpload}
                  disabled={uploading || layers.size === 0}
                  className="mt-6 w-full bg-gold hover:bg-yellow-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-lg transition-all"
                >
                  {uploading ? 'Uploading...' : 'Upload Project Layers'}
                </button>
              </>
            )}
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-4 bg-red-900/30 border border-red-500 rounded-lg text-red-200 mb-4"
            >
              {error}
            </motion.div>
          )}

          {success && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-4 bg-green-900/30 border border-green-500 rounded-lg text-green-200"
            >
              {success}
            </motion.div>
          )}
        </motion.div>
      </div>
    </BlacksmithWorkshop>
  );
}

